import React, { useState } from 'react';
import { Chip, Modal, formatDateToShortDotV2, useAuth, useT } from '@repo/common';
import { ReactComponent as OptionIcon } from '@repo/common/assets/icons/20px/icon_20px_option_filled.svg';
import UserOptionList from './UserOptionList';
import { USER_COLUMN_CLASS } from './userColumns';
import {
  setAdminUserBlocked,
  setAdminUserBusiness,
  setAdminUserCreator,
} from '../../../api/user/user.api';

/*
 * 상태 배지 — 워크숍 쪽과 같은 방식으로 공통 Chip 을 색만 바꿔 쓴다.
 * @param {string} tone 배경·글자색 Tailwind 클래스 (예: 'bg-green-light02 text-green-deep04')
 */
const Badge = ({ tone, title, children }) => (
  <span title={title} className='inline-flex'>
    <Chip
      type='fill'
      state='disabled'
      size='xs'
      hideIcon
      disableTypography
      disableDefaultColors
      bgclass={`h-auto rounded-full px-2 py-0.5 ${tone}`}
      borderclass='ring-0'
      textclass='typo-b12Strong whitespace-nowrap'
    >
      {children}
    </Chip>
  </span>
);

// 서버에는 영문 사유를 그대로 저장한다 — 관리자끼리 읽는 값이라 번역하지 않는다
const BLOCK_REASONS = [
  { value: 'Policy violation', labelKr: '이용약관 위반' },
  { value: 'Spam or abuse', labelKr: '스팸 · 악용' },
  { value: 'Fraud or payment issue', labelKr: '사기 · 결제 문제' },
  { value: 'Inappropriate content', labelKr: '부적절한 콘텐츠' },
  { value: 'Other', labelKr: '기타' },
];

/*
 * 확인 모달과 완료 토스트 문구.
 * 해제·차단해도 강의·정산·기업 정보는 서버가 지우지 않으므로 그 점을 함께 알린다.
 */
const buildAction = (t, { type, enabled, name }) => {
  if (type === 'block') {
    return enabled
      ? {
          title: t('Block this account?', '이 계정을 차단할까요?'),
          subText: t(
            `${name} will be signed out and won't be able to log in until unblocked. Their data stays as it is.`,
            `${name}님은 로그아웃되고, 해제할 때까지 로그인할 수 없어요. 데이터는 그대로 남아요.`,
          ),
          confirmLabel: t('Block', '차단'),
          doneMessage: t('Account blocked.', '계정을 차단했어요.'),
        }
      : {
          title: t('Unblock this account?', '차단을 해제할까요?'),
          subText: t(
            `${name} will be able to log in again right away.`,
            `${name}님이 바로 다시 로그인할 수 있게 돼요.`,
          ),
          confirmLabel: t('Unblock', '해제'),
          doneMessage: t('Account unblocked.', '차단을 해제했어요.'),
        };
  }
  if (type === 'creator') {
    return enabled
      ? {
          title: t('Grant Creator role?', '크리에이터 역할을 부여할까요?'),
          subText: t(
            `${name} will be able to build and publish courses on Koready Skills.`,
            `${name}님이 Koready Skills에서 강의를 만들고 게시할 수 있게 돼요.`,
          ),
          confirmLabel: t('Grant', '부여'),
          doneMessage: t('Creator role granted.', '크리에이터 역할을 부여했어요.'),
        }
      : {
          title: t('Revoke Creator role?', '크리에이터 역할을 해제할까요?'),
          subText: t(
            `${name} will lose access to Creator Studio. Their courses and payout info stay as they are.`,
            `${name}님은 크리에이터 스튜디오를 더 이상 쓸 수 없어요. 강의와 정산 정보는 그대로 남아요.`,
          ),
          confirmLabel: t('Revoke', '해제'),
          doneMessage: t('Creator role revoked.', '크리에이터 역할을 해제했어요.'),
        };
  }
  return enabled
    ? {
        title: t('Switch to Business account?', '기업 계정으로 전환할까요?'),
        subText: t(
          `${name} will get the business menus. They may still need to fill in company details.`,
          `${name}님에게 기업 메뉴가 열려요. 회사 정보는 따로 입력해야 할 수 있어요.`,
        ),
        confirmLabel: t('Switch', '전환'),
        doneMessage: t('Switched to Business account.', '기업 계정으로 전환했어요.'),
      }
    : {
        title: t('Revoke Business account?', '기업 계정을 해제할까요?'),
        subText: t(
          `${name} will be switched to a personal account. Their company info and job posts stay as they are.`,
          `${name}님은 개인 계정으로 바뀌어요. 기업 정보와 채용 공고는 그대로 남아요.`,
        ),
        confirmLabel: t('Revoke', '해제'),
        doneMessage: t('Business account revoked.', '기업 계정을 해제했어요.'),
      };
};

/** 계정 유형 배지 묶음 — 표 행과 카드가 같이 쓴다 */
const AccountBadges = ({ user, t }) => {
  const isAdmin = user.role === 'A';
  const isBusiness = user.role === 'E';

  if (user.deleted) {
    return (
      <Badge tone='bg-grayscale-gray02 text-text-secondary'>
        {t('Deleted', '탈퇴')}
      </Badge>
    );
  }
  return (
    <>
      {user.blocked && (
        <Badge
          tone='bg-functional-failed01 text-text-failed'
          title={[
            user.blockedReason,
            user.blockedAt && formatDateToShortDotV2(user.blockedAt),
          ]
            .filter(Boolean)
            .join(' · ')}
        >
          {t('Blocked', '차단')}
        </Badge>
      )}
      {isAdmin && (
        <Badge tone='bg-grayscale-gray07 text-text-inverse'>
          {t('Admin', '관리자')}
        </Badge>
      )}
      {isBusiness && (
        <Badge tone='bg-purple-purple01 text-purple-purple05'>
          {t('Business', '기업')}
        </Badge>
      )}
      {user.creator && (
        <Badge tone='bg-green-light02 text-green-deep04'>
          {t('Creator', '크리에이터')}
        </Badge>
      )}
      {!isAdmin && !isBusiness && !user.creator && !user.blocked && (
        <span className='typo-b13 text-text-placeholder'>
          {t('Personal', '개인')}
        </span>
      )}
    </>
  );
};

const UserRow = ({ user, onUpdated, onFailed }) => {
  const t = useT();
  const { user: me } = useAuth();
  const [showOptionList, setShowOptionList] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [blockReason, setBlockReason] = useState(BLOCK_REASONS[0].value);
  const [isSaving, setIsSaving] = useState(false);

  const fullName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') || '-';
  const isAdmin = user.role === 'A';
  const isBusiness = user.role === 'E';
  const isMe = me?.id === user.id;

  const openAction = (type, enabled) => {
    setShowOptionList(false);
    setBlockReason(BLOCK_REASONS[0].value);
    setPendingAction({ type, enabled, ...buildAction(t, { type, enabled, name: fullName }) });
  };

  const handleConfirm = async () => {
    if (!pendingAction || isSaving) return;
    setIsSaving(true);
    try {
      const { type, enabled } = pendingAction;
      if (type === 'block') {
        await setAdminUserBlocked(user.id, { blocked: enabled, reason: enabled ? blockReason : '' });
      } else if (type === 'creator') {
        await setAdminUserCreator(user.id, enabled);
      } else {
        await setAdminUserBusiness(user.id, enabled);
      }
      onUpdated?.(pendingAction.doneMessage);
      setPendingAction(null);
    } catch (error) {
      console.error('회원 계정을 변경하지 못했습니다.', error);
      onFailed?.(
        error?.response?.data?.msg ||
          t('Could not update this account. Please try again.', '계정을 변경하지 못했어요. 다시 시도해 주세요.'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const options = [
    {
      key: 'creator',
      label: user.creator
        ? t('Revoke Creator role', '크리에이터 해제')
        : t('Grant Creator role', '크리에이터 부여'),
      danger: user.creator,
      onSelect: () => openAction('creator', !user.creator),
    },
  ];
  // 관리자 계정은 서버가 기업 전환을 무시하므로 메뉴에서 뺀다
  if (!isAdmin) {
    options.push({
      key: 'business',
      label: isBusiness
        ? t('Revoke Business account', '기업 계정 해제')
        : t('Switch to Business account', '기업 계정으로 전환'),
      danger: isBusiness,
      onSelect: () => openAction('business', !isBusiness),
    });
  }
  // 관리자와 본인은 서버가 차단을 거부한다
  if (!isAdmin && !isMe) {
    options.push({
      key: 'block',
      label: user.blocked
        ? t('Unblock account', '차단 해제')
        : t('Block account', '계정 차단'),
      danger: !user.blocked,
      onSelect: () => openAction('block', !user.blocked),
    });
  }

  const isBlockModal = pendingAction?.type === 'block' && pendingAction.enabled;
  const blockReasonLabel =
    BLOCK_REASONS.find((reason) => reason.value === blockReason) ?? BLOCK_REASONS[0];

  const optionButton = !user.deleted && (
    <button
      type='button'
      aria-label={t('Account options', '계정 옵션')}
      className='text-icon-placeholder hover:text-icon-tertiary active:text-icon-tertiary'
      onClick={(e) => {
        e.stopPropagation();
        setAnchorRect(e.currentTarget.getBoundingClientRect());
        setShowOptionList(true);
      }}
    >
      <OptionIcon />
    </button>
  );

  const joined = formatDateToShortDotV2(user.createdAt);
  const lastLogin = user.lastLoginAt ? formatDateToShortDotV2(user.lastLoginAt) : '-';

  return (
    <div className='w-full'>
      {/* 표 행 — tabletV(1180px) 이상 */}
      <div className='hidden tabletV:flex w-full h-[48px] items-center bg-grayscale-gray00'>
        <div className={`flex justify-center items-center p-1 ${USER_COLUMN_CLASS.joined}`}>
          <span className='typo-b13 text-text-disable block w-full text-center truncate'>
            {joined}
          </span>
        </div>
        <div className={`flex justify-center items-center p-1 ${USER_COLUMN_CLASS.id}`}>
          <span className='typo-b14 text-text-secondary'>#{user.id}</span>
        </div>
        <div className={`flex flex-col justify-center items-center p-1 text-center ${USER_COLUMN_CLASS.name}`}>
          <span className='typo-b14Strong text-text-primary truncate block w-full'>
            {fullName}
          </span>
          {user.companyName && (
            <span className='typo-b11 text-text-placeholder truncate block w-full'>
              {user.companyName}
            </span>
          )}
        </div>
        <div className={`flex justify-center items-center p-1 ${USER_COLUMN_CLASS.email}`}>
          <span
            className='typo-b14 text-text-secondary truncate block w-full text-center'
            title={user.email || ''}
          >
            {user.email || '-'}
          </span>
        </div>
        <div className={`flex flex-wrap justify-center items-center gap-1 p-1 overflow-hidden ${USER_COLUMN_CLASS.type}`}>
          <AccountBadges user={user} t={t} />
        </div>
        <div className={`flex justify-center items-center p-1 ${USER_COLUMN_CLASS.lastLogin}`}>
          <span className='typo-b13 text-text-disable'>{lastLogin}</span>
        </div>
        <div className={`flex justify-center items-center px-0.5 ${USER_COLUMN_CLASS.options}`}>
          {optionButton}
        </div>
      </div>

      {/* 카드 — tabletV 미만 */}
      <div className='tabletV:hidden flex flex-col gap-2 w-full px-3 py-3 rounded-2xl border border-grayscale-gray02 bg-grayscale-gray00'>
        <div className='flex items-start justify-between gap-2'>
          <div className='flex flex-col min-w-0'>
            <span className='typo-b14Strong text-text-primary truncate'>{fullName}</span>
            {user.companyName && (
              <span className='typo-b12 text-text-placeholder truncate'>{user.companyName}</span>
            )}
            <span className='typo-b13 text-text-secondary break-all' title={user.email || ''}>
              {user.email || '-'}
            </span>
          </div>
          <div className='flex-shrink-0 -mr-1'>{optionButton}</div>
        </div>
        <div className='flex flex-wrap items-center gap-1'>
          <AccountBadges user={user} t={t} />
        </div>
        <div className='flex flex-wrap gap-x-3 gap-y-0.5 typo-b12 text-text-disable'>
          <span>#{user.id}</span>
          <span>
            {t('Joined', '가입')} {joined}
          </span>
          <span>
            {t('Last login', '최근 로그인')} {lastLogin}
          </span>
        </div>
      </div>

      {showOptionList && (
        <UserOptionList
          anchorRect={anchorRect}
          options={options}
          onClose={() => setShowOptionList(false)}
        />
      )}

      {pendingAction && (
        <Modal
          isOpen
          type={pendingAction.enabled && pendingAction.type !== 'block' ? 'default' : 'warning'}
          title={pendingAction.title}
          subText={pendingAction.subText}
          leftButtonLabel={t('Cancel', '취소')}
          rightButtonLabel={pendingAction.confirmLabel}
          rightButtonLoading={isSaving}
          onClickLeftBtn={() => !isSaving && setPendingAction(null)}
          onClickRightBtn={handleConfirm}
          showDropDown={isBlockModal}
          dropDownValue={isBlockModal ? t(blockReasonLabel.value, blockReasonLabel.labelKr) : ''}
          onDropDownChange={setBlockReason}
          dropDownOptions={BLOCK_REASONS.map(({ value, labelKr }) => ({
            value,
            label: t(value, labelKr),
          }))}
        />
      )}
    </div>
  );
};

export default UserRow;
